import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowUp, FaCheck } from "react-icons/fa";
import { getRequest, postRequest } from "../../apiWrapper";
import { gradeOptions, gradeTabs, filterStudentsFromGrade } from "../../constants/gradeOptions";

const AdminTeacherAssignments = () => {
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState({});
  const [studentColors, setStudentColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState("all");
  
  const filteredStudents = filterStudentsFromGrade(students, selectedGrade);

  // 設定から生徒カラーを取得
  useEffect(() => {
    const fetchStudentColors = async () => {
      try {
        const data = await getRequest("settings/default");
        if (data && data.colors && data.colors.studentColors) {
          setStudentColors(data.colors.studentColors);
        }
      } catch (error) {
        console.error("❌ 設定データの取得エラー", error);
      }
    };
    fetchStudentColors();
  }, []);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const data = await getRequest("teachers?excludeRetired=true");
        if (data && data.teachers) {
          setTeachers(data.teachers);
        }
      } catch (error) {
        console.error("❌ 教師データ取得エラー:", error);
      }
    };
    fetchTeachers();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const data = await getRequest("students");
        if (data && data.students) {
          setStudents(data.students);
        }
      } catch (error) {
        console.error("❌ 生徒データ取得エラー:", error);
      }
    };
    fetchStudents();
  }, []);

  const handleTeacherSelect = async (teacherid) => {
    if (!teacherid) {
      setSelectedTeacher("");
      setAssignedStudents({});
      return;
    }

    setSelectedTeacher(teacherid);
    setLoading(true);

    try {
      const data = await getRequest(`teacherAssignments?uid=${teacherid}`);
      // API から取得したデータが存在する場合、適切にセット
      setAssignedStudents(data && data.students ? data.students : {}); // 存在しない場合は空オブジェクトをセット
    } catch (error) {
      console.error("❌ データ取得時にエラー:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentToggle = (studentid) => {
    setAssignedStudents((prev) => {
      if (prev[studentid]) {
        const updated = { ...prev };
        delete updated[studentid];
        return updated;
      } else {
        return { ...prev, [studentid]: { subjects: [], color: "custom1" } };
      }
    });
  };

  const handleSubjectToggle = (studentid, subject) => {
    setAssignedStudents((prev) => {
      const studentData = prev[studentid] || { subjects: [], color: "custom1" };
      const newSubjects = studentData.subjects.includes(subject)
        ? studentData.subjects.filter((s) => s !== subject)
        : [...studentData.subjects, subject];
      return {
        ...prev,
        [studentid]: { ...studentData, subjects: newSubjects },
      };
    });
  };

  const handleColorChange = (studentid, colorKey) => {
    setAssignedStudents((prev) => ({
      ...prev,
      [studentid]: {
        ...prev[studentid],
        color: colorKey,
      },
    }));
  };

  const saveAssignments = async () => {
    if (!selectedTeacher) return;

    try {
      const response = await postRequest("teacherAssignments", {
        uid: selectedTeacher,
        students: assignedStudents,
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("担当生徒の割り当てを保存しました！", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else {
        toast.error("割り当ての保存に失敗しました。");
      }
    } catch (error) {
      console.error("❌ 保存エラー:", error);
      toast.error("割り当ての保存に失敗しました。");
    }
  };

  // 生徒一覧を現役（withdrawn===false）と退会（withdrawn===true）に振り分け
  const activeStudents = filteredStudents.filter((student) => !student.withdrawn);
  const withdrawnStudents = filteredStudents.filter((student) => student.withdrawn);

  return (
    <div className="p-2 pt-10 relative pb-24 max-w-7xl mx-auto">
      {/* sticky ヘッダー */}
      <div className="sticky top-0 bg-white shadow-md p-4 z-30">
        <h2 className="text-2xl font-semibold mb-2">講師の担当生徒管理</h2>
        <select
          className="border p-2 w-full"
          onChange={(e) => handleTeacherSelect(e.target.value)}
          value={selectedTeacher || ""}
        >
          <option value="">講師を選択</option>
          {teachers.map((teacher) => (
            <option key={teacher.id} value={teacher.id}>
              {teacher.name}
            </option>
          ))}
        </select>
      </div>

      <div className="pt-6">
        {loading && <p>データを取得中...</p>}

        {/* 学年フィルタ（PC: タブ / モバイル: ドロップダウン） */}
        <div className="mb-4">
          <div className="hidden md:flex space-x-2 overflow-x-auto pb-2">
            {gradeTabs.map((opt) => (
              <button
                key={opt.value}
                className={`px-4 py-2 rounded-lg transition ${selectedGrade === opt.value ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                onClick={() => setSelectedGrade(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="md:hidden">
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="w-full p-2 border rounded"
            >
              {gradeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedTeacher && !loading && (
          <div className="bg-white p-1">
            <h3 className="text-lg font-semibold mb-2">現役生徒一覧</h3>
            {activeStudents.map((student) => (
              <div
                key={student.id}
                className="mb-4 bg-gray-50 border p-4 rounded-lg shadow-md"
              >
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!assignedStudents[student.id]}
                    onChange={() => handleStudentToggle(student.id)}
                    className="mr-2"
                  />
                  {student.name}
                </label>
                {assignedStudents[student.id] && (
                  <>
                    <div className="ml-6 flex flex-wrap">
                      {["算数", "数学", "英語", "国語", "理科", "社会"].map(
                        (subject) => (
                          <label key={subject} className="flex items-center mr-4">
                            <input
                              type="checkbox"
                              checked={
                                (assignedStudents[student.id]?.subjects ||
                                  []).includes(subject)
                              }
                              onChange={() =>
                                handleSubjectToggle(student.id, subject)
                              }
                              className="mr-1"
                            />
                            {subject}
                          </label>
                        )
                      )}
                    </div>
                    <div className="ml-6 mt-2">
                      <span className="mr-2">タブの色:</span>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(studentColors).map(
                          ([key, color]) => (
                            <div
                              key={key}
                              className={`w-8 h-8 rounded-full cursor-pointer border-2 transition-all flex items-center justify-center ${assignedStudents[student.id]?.color === key
                                ? "border-black scale-110"
                                : "border-gray-300"
                                }`}
                              style={{ backgroundColor: color }}
                              onClick={() =>
                                handleColorChange(student.id, key)
                              }
                            >
                              {assignedStudents[student.id]?.color === key && (
                                <FaCheck className="text-white" />
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}

            <h3 className="text-lg font-semibold mb-2 mt-8">退会生徒一覧</h3>
            {withdrawnStudents.map((student) => (
              <div
                key={student.id}
                className="mb-4 bg-gray-50 border p-4 rounded-lg shadow-md"
              >
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!assignedStudents[student.id]}
                    onChange={() => handleStudentToggle(student.id)}
                    className="mr-2"
                  />
                  {student.name} <span className="text-sm text-gray-600">(退会)</span>
                </label>
                {assignedStudents[student.id] && (
                  <>
                    <div className="ml-6 flex flex-wrap">
                      {["算数", "数学", "英語", "国語", "理科", "社会"].map(
                        (subject) => (
                          <label key={subject} className="flex items-center mr-4">
                            <input
                              type="checkbox"
                              checked={
                                (assignedStudents[student.id]?.subjects ||
                                  []).includes(subject)
                              }
                              onChange={() =>
                                handleSubjectToggle(student.id, subject)
                              }
                              className="mr-1"
                            />
                            {subject}
                          </label>
                        )
                      )}
                    </div>
                    <div className="ml-6 mt-2">
                      <span className="mr-2">タブの色:</span>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(studentColors).map(
                          ([key, color]) => (
                            <div
                              key={key}
                              className={`w-8 h-8 rounded-full cursor-pointer border-2 transition-all flex items-center justify-center ${assignedStudents[student.id]?.color === key
                                ? "border-black scale-110"
                                : "border-gray-300"
                                }`}
                              style={{ backgroundColor: color }}
                              onClick={() =>
                                handleColorChange(student.id, key)
                              }
                            >
                              {assignedStudents[student.id]?.color === key && (
                                <FaCheck className="text-white" />
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 固定保存ボタン */}
      {selectedTeacher && (
        <button
          className="fixed bottom-0 left-0 w-full lg:left-64 lg:w-[calc(100%-16rem)] bg-blue-500 text-white py-3 text-lg font-bold shadow-md hover:bg-blue-600 transition"
          onClick={saveAssignments}
        >
          保存
        </button>

      )}

      <button
        className="fixed bottom-16 right-10 bg-blue-500 text-white p-3 rounded-full shadow-lg hover:bg-blue-600 transition"
        onClick={() =>
          window.scrollTo({ top: 0, behavior: "smooth" })
        }
      >
        <FaArrowUp size={24} />
      </button>
    </div>
  );
};

export default AdminTeacherAssignments;
